import { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import Table from "../../../common/Table/Table";
import { errorToast, successToast } from "../../../react-toastfiy/toast";
import taUserManagementService from "../../../services/channelFinance/taUserManagement.service";
import {
    Field,
    StatusBadge,
    formatDate,
    formatDaysToExpiry,
    indianCurrency,
} from "./taUserHelpers";

const RENEWAL_FILE_FIELDS = [
    { name: "renewal_request_letter", label: "Renewal Request Letter", multiple: false },
    { name: "renewal_sanction_letter", label: "Renewal Sanction Letter", multiple: false },
    { name: "renewal_other_doc", label: "Other Documents", multiple: true },
];

/** Day after the current expiry, as YYYY-MM-DD, for the date input's `min`. */
function minExpiry(currentExpiry) {
    if (!currentExpiry) return undefined;
    const d = new Date(currentExpiry);
    if (isNaN(d.getTime())) return undefined;
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
}

function ContextBanner({ user, lastRenewalId }) {
    const cells = [
        ["Name", user.name],
        ["Phone", user.phone],
        ["State", user.state || "—"],
        ["Current LOS / Renewal ID", lastRenewalId || "—"],
        ["Current Expiry", formatDate(user.ta_limit_expiry_date)],
        ["Credit Limit", indianCurrency(user.tc_credit_display_limit)],
        ["Vehicle Limit", user.tc_vehicle_limit ?? "—"],
    ];
    return (
        <div style={{
            padding: "12px 16px", background: "#f0f9ff", border: "1.5px solid #bae6fd",
            borderRadius: 10, marginBottom: 20, display: "flex", gap: 24, flexWrap: "wrap",
        }}>
            {cells.map(([k, v]) => (
                <div key={k}>
                    <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>{k}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{v}</div>
                </div>
            ))}
            <div>
                <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Days to Expiry</div>
                <div style={{ marginTop: 2, display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                        {formatDaysToExpiry(user.ta_limit_days_to_expiry)}
                    </span>
                    <StatusBadge status={user.ta_limit_status} />
                </div>
            </div>
        </div>
    );
}

const HISTORY_COLUMNS = [
    {
        title: "Renewal App ID",
        data_key: "renewalAppId",
        render: (row) => (
            <div>
                <div style={{ fontWeight: 700, color: "#0f172a", display: "flex", alignItems: "center", gap: 6 }}>
                    {row.renewalAppId}
                    {row.isEarlyRenewal && (
                        <span style={{
                            fontSize: 10, fontWeight: 800, padding: "2px 7px", borderRadius: 12,
                            background: "#fef3c7", color: "#b45309", border: "1px solid #fde68a",
                        }}>Early</span>
                    )}
                </div>
                {row.remarks && <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 2 }}>{row.remarks}</div>}
            </div>
        ),
    },
    {
        title: "Expiry",
        data_key: "newExpiryDate",
        render: (row) => (
            <span style={{ whiteSpace: "nowrap" }}>
                <span style={{ color: "#94a3b8" }}>{formatDate(row.previousExpiryDate)}</span>
                {" \u2192 "}
                <strong style={{ color: "#0f172a" }}>{formatDate(row.newExpiryDate)}</strong>
            </span>
        ),
    },
    {
        title: "Previous LOS ID",
        data_key: "previousLosApplicationId",
        render: (row) => row.previousLosApplicationId || "\u2014",
    },
    {
        title: "Documents",
        data_key: "documents",
        render: (row) => (
            row.documents?.length
                ? row.documents.map((d) => (
                    <a key={d.id} href={d.documentPath} target="_blank" rel="noreferrer" style={{ display: "block" }}>
                        {d.fileName}
                    </a>
                ))
                : <span style={{ color: "#94a3b8" }}>None</span>
        ),
    },
    {
        title: "By / On",
        data_key: "renewedByName",
        render: (row) => (
            <span style={{ whiteSpace: "nowrap" }}>
                {row.renewedByName || "\u2014"}
                <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 2 }}>{row.renewedAt}</div>
            </span>
        ),
    },
];

function RenewalHistory({ userId }) {
    const [open, setOpen] = useState(false);
    const [rows, setRows] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || rows !== null) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await taUserManagementService.getRenewalHistory(userId);
                if (!cancelled) setRows(res?.status === 200 ? res.data : []);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, rows, userId]);

    return (
        <div style={{ marginTop: 20 }}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%",
                    background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 10,
                    padding: "12px 16px", cursor: "pointer", textAlign: "left",
                }}
            >
                <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                    Renewal history{rows ? ` (${rows.length})` : ""}
                </span>
                <span style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>
                    {open ? "Hide ▴" : "Show ▾"}
                </span>
            </button>

            {open && (
                <div style={{ border: "1.5px solid #e2e8f0", borderTop: 0, borderRadius: "0 0 10px 10px" }}>
                    {!loading && rows?.length === 0 ? (
                        <div style={{ padding: 16, fontSize: 13, color: "#64748b" }}>No previous renewals.</div>
                    ) : (
                        <Table
                            data={rows || []}
                            loading={loading}
                            loading_rows={2}
                            no_data_msg="No previous renewals."
                            columns={HISTORY_COLUMNS}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

export default function TARenewLimitModal({ show, user, onHide, onSuccess }) {
    const [form, setForm] = useState({ renewalAppId: "", newExpiryDate: "", remarks: "" });
    const [files, setFiles] = useState({});
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    // Set when the API answers 409: renewing >30 days out needs an explicit tick.
    const [earlyPrompt, setEarlyPrompt] = useState(null);
    const [earlyConfirmed, setEarlyConfirmed] = useState(false);
    const fileInputs = useRef({});

    const daysOut = user?.ta_limit_days_to_expiry;
    const looksEarly = typeof daysOut === "number" && daysOut > 30;
    const min = useMemo(() => minExpiry(user?.ta_limit_expiry_date), [user]);

    const handleChange = (name, value) => {
        setForm((f) => ({ ...f, [name]: value }));
        setErrors((e) => ({ ...e, [name]: undefined }));
    };

    const handleFiles = (name, fileList) => {
        setFiles((f) => ({ ...f, [name]: Array.from(fileList || []) }));
    };

    const removeFile = (name, idx) => {
        setFiles((f) => {
            const next = (f[name] || []).filter((_, i) => i !== idx);
            if (next.length === 0 && fileInputs.current[name]) fileInputs.current[name].value = "";
            return { ...f, [name]: next };
        });
    };

    const validate = () => {
        const errs = {};
        if (!form.renewalAppId.trim()) errs.renewalAppId = "Required";
        if (!form.newExpiryDate) errs.newExpiryDate = "Required";
        else if (min && form.newExpiryDate < min) {
            errs.newExpiryDate = `Must be after ${formatDate(user.ta_limit_expiry_date)}`;
        }
        return errs;
    };

    const submit = async (confirmEarly) => {
        const errs = validate();
        if (Object.keys(errs).length) { setErrors(errs); return; }

        setSaving(true);
        try {
            const fd = new FormData();
            fd.append("userId", user.id);
            fd.append("renewalAppId", form.renewalAppId.trim());
            fd.append("newExpiryDate", form.newExpiryDate);
            if (form.remarks.trim()) fd.append("remarks", form.remarks.trim());
            if (confirmEarly) fd.append("confirmEarlyRenewal", "true");
            RENEWAL_FILE_FIELDS.forEach(({ name }) =>
                (files[name] || []).forEach((f) => fd.append(name, f)));

            const res = await taUserManagementService.renewLimit(fd);

            if (res?.status === 200) {
                // The renewed user drops out of the expiry list, which only holds
                // limits inside 30 days — say so, or the row seems to vanish.
                // Say how many documents landed: renewal documents are optional,
                // so a silent zero is indistinguishable from a failed upload.
                const attached = RENEWAL_FILE_FIELDS.reduce(
                    (n, { name }) => n + (files[name]?.length || 0), 0);
                const stored = res.documentsUploaded ?? 0;
                const docNote = attached === 0
                    ? ""
                    : stored === attached
                        ? ` ${stored} document${stored === 1 ? "" : "s"} uploaded.`
                        : ` Warning: ${attached} file${attached === 1 ? "" : "s"} selected but ${stored} stored.`;
                successToast(`${res.msg}${docNote} ${user.name} is no longer expiring soon.`);
                onSuccess?.(res);
                return;
            }
            if (res?.status === 409 && res?.requiresEarlyConfirmation) {
                setEarlyPrompt(res);
                return;
            }
            if (/Renewal App ID already exists/i.test(res?.msg || "")) {
                setErrors((e) => ({ ...e, renewalAppId: res.msg }));
                return;
            }
            errorToast(res?.msg || "Failed to renew limit");
        } catch (e) {
            errorToast("Failed to renew limit");
        } finally {
            setSaving(false);
        }
    };

    const showEarlyBlock = Boolean(earlyPrompt) || looksEarly;
    const blocked = showEarlyBlock && !earlyConfirmed;

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton style={{ background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0" }}>
                <Modal.Title as="div" style={{ fontSize: "1.5rem", fontWeight: 600 }}>
                    Renew TA Limit — {user.name}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ padding: 24 }}>
                <ContextBanner user={user} lastRenewalId={user.last_renewal_app_id || user.los_application_id} />

                <div className="row g-3">
                    <Field
                        label="Renewal App ID" name="renewalAppId" required
                        value={form.renewalAppId} onChange={handleChange} errors={errors}
                        placeholder="e.g. REN-2027-0091"
                    />
                    <Field
                        label="Updated Expiry Date" name="newExpiryDate" required
                        value={form.newExpiryDate} onChange={handleChange} errors={errors}
                    >
                        <input
                            id="newExpiryDate"
                            type="date"
                            min={min}
                            className={`form-control ${errors.newExpiryDate ? "is-invalid" : ""}`}
                            value={form.newExpiryDate}
                            onChange={(e) => handleChange("newExpiryDate", e.target.value)}
                            style={{ borderRadius: 8 }}
                        />
                    </Field>
                    <Field label="Remarks" name="remarks" className="col-12" value={form.remarks} onChange={handleChange} errors={errors}>
                        <textarea
                            id="remarks" rows={2} className="form-control"
                            value={form.remarks}
                            onChange={(e) => handleChange("remarks", e.target.value)}
                            placeholder="Optional"
                            style={{ borderRadius: 8, resize: "vertical" }}
                        />
                    </Field>
                </div>

                <div style={{ marginTop: 20 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#212529", marginBottom: 10, display: "flex", gap: 8, alignItems: "center" }}>
                        Documents
                        <span style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>All optional</span>
                    </div>
                    <div className="row g-3">
                        {RENEWAL_FILE_FIELDS.map(({ name, label, multiple }) => (
                            <div key={name} className={multiple ? "col-12" : "col-md-6"}>
                                <label className="form-label" htmlFor={name} style={{ fontSize: 12, fontWeight: 700 }}>{label}</label>
                                <input
                                    id={name}
                                    ref={(el) => { fileInputs.current[name] = el; }}
                                    type="file"
                                    multiple={multiple}
                                    className="form-control"
                                    style={{ borderRadius: 8 }}
                                    onChange={(e) => handleFiles(name, e.target.files)}
                                />
                                {(files[name] || []).length > 0 && (
                                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                                        {files[name].map((f, i) => (
                                            <span key={`${f.name}-${i}`} style={{
                                                fontSize: 12, fontWeight: 600, color: "#0f172a", background: "#f1f5f9",
                                                border: "1px solid #e2e8f0", borderRadius: 16, padding: "4px 8px 4px 12px",
                                                display: "inline-flex", alignItems: "center", gap: 8,
                                            }}>
                                                {f.name}
                                                <button
                                                    type="button"
                                                    aria-label={`Remove ${f.name}`}
                                                    onClick={() => removeFile(name, i)}
                                                    style={{ border: 0, background: "transparent", color: "#64748b", cursor: "pointer", fontSize: 14, lineHeight: 1, padding: "0 2px" }}
                                                >×</button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                <div style={{ fontSize: 12, color: "#64748b", borderTop: "1px solid #f1f5f9", paddingTop: 14, marginTop: 18 }}>
                    Renewal changes the expiry date only. Credit limit, vehicle limit and margins are unchanged.
                </div>

                {showEarlyBlock && (
                    <div style={{
                        background: "#fef3c7", border: "1.5px solid #fde68a", borderRadius: 10,
                        padding: "14px 16px", marginTop: 16, display: "flex", gap: 12, alignItems: "flex-start",
                    }}>
                        <input
                            id="confirmEarlyRenewal" type="checkbox" checked={earlyConfirmed}
                            onChange={(e) => setEarlyConfirmed(e.target.checked)}
                            style={{ width: 17, height: 17, marginTop: 1, flexShrink: 0, accentColor: "#b45309" }}
                        />
                        <label htmlFor="confirmEarlyRenewal" style={{ fontSize: 13, color: "#b45309", lineHeight: 1.5, cursor: "pointer" }}>
                            <strong style={{ fontWeight: 800 }}>
                                This limit expires on {formatDate(earlyPrompt?.currentExpiryDate || user.ta_limit_expiry_date)}
                                , {earlyPrompt?.daysToExpiry ?? daysOut} days from now.
                            </strong>{" "}
                            Renewing more than 30 days early is recorded against this cycle. Confirm early renewal.
                        </label>
                    </div>
                )}

                <RenewalHistory userId={user.id} />
            </Modal.Body>

            <Modal.Footer style={{ background: "#f8fafc", borderTop: "1.5px solid #e2e8f0", gap: 8 }}>
                {blocked && (
                    <span style={{ fontSize: 12, color: "#94a3b8", marginRight: "auto" }}>
                        Tick the box to enable renewal
                    </span>
                )}
                <button className="btn btn-light" style={{ borderRadius: 8, fontWeight: 700, fontSize: 13 }}
                    onClick={onHide} disabled={saving}>Cancel</button>
                <button
                    className="btn btn-primary"
                    style={{ borderRadius: 8, fontWeight: 700, fontSize: 13, minWidth: 110 }}
                    disabled={saving || blocked}
                    onClick={() => submit(earlyConfirmed)}
                >
                    {saving ? <Spinner animation="border" size="sm" /> : "Renew Limit"}
                </button>
            </Modal.Footer>
        </Modal>
    );
}
